import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { api } from '@consts/url.const';
import { PersonGroupsModel } from '@models/person-groups.model';
import { PersonModel } from '@models/person.model';

@Injectable({
  providedIn: 'root',
})
export class PersonGroupsService {
  private readonly http = inject(HttpClient);

  public getPersonGroups(): Observable<PersonGroupsModel.PersonGroupListDocument[]> {
    return this.http
      .get<PersonGroupsModel.API.PersonGroupListItem[]>(api.personGroups.list);
  }

  public getPersonGroupDetails(groupId: string): Observable<PersonGroupsModel.PersonGroupDetailsDocument> {
    return this.http
      .get<PersonGroupsModel.API.PersonGroupDetails>(api.personGroups.details(groupId))
      .pipe(
        map(
          (group): PersonGroupsModel.PersonGroupDetailsDocument => ({
            _id: group._id,
            name: group.name,
            personIds: group.personIds,
            createdByOrgId: group.createdByOrgId,
            accessList: group.accessList,
            personsCounter: group.personsCounter,
            accessListCounter: group.accessListCounter,
            activeOrgAccessRole: group.activeOrgAccessRole,
          }),
        ),
      );
  }

  public getPersonGroupPersons(groupId: string): Observable<PersonModel.SavedPerson[]> {
    return this.http
      .get<PersonGroupsModel.API.GroupPersonsResponse>(api.personGroups.persons(groupId))
      .pipe(map((response) => response.persons));
  }

  public joinPersonGroup(code: string): Observable<PersonGroupsModel.PersonGroupListDocument> {
    const trimmed = code.trim();
    return this.http.post<PersonGroupsModel.API.PersonGroupListItem>(api.personGroups.join, { inviteCode: trimmed });
  }

  public createPersonGroup(name: string): Observable<PersonGroupsModel.PersonGroupListDocument> {
    const trimmed = name.trim();
    return this.http.post<PersonGroupsModel.API.PersonGroupListItem>(api.personGroups.create, { name: trimmed });
  }

  public updatePersonGroup(
    id: string,
    name: string,
  ): Observable<PersonGroupsModel.PersonGroupListDocument> {
    const trimmed = name.trim();
    return this.http.patch<PersonGroupsModel.API.PersonGroupListItem>(api.personGroups.update(id), {
      name: trimmed,
    });
  }

  public deletePersonGroup(id: string): Observable<void> {
    return this.http.delete<void>(api.personGroups.update(id));
  }

  public loadPersonGroupAccessList(groupId: string): Observable<PersonGroupsModel.API.AccessListResponse> {
    return this.http.get<PersonGroupsModel.API.AccessListResponse>(api.personGroups.accessList(groupId));
  }

  public loadPersonGroupInviteCode(
    groupId: string,
    accessType: PersonGroupsModel.ShareAccessRole,
  ): Observable<string> {
    return this.http
      .get<PersonGroupsModel.API.InviteCodeResponse>(api.personGroups.inviteCode(groupId, accessType))
      .pipe(map((response) => response.inviteCode));
  }

  public updatePersonGroupAccessListItem(
    groupId: string,
    orgId: string,
    accessType: PersonGroupsModel.ShareAccessRole,
    previous: PersonGroupsModel.PersonGroupSharedWithRow,
  ): Observable<PersonGroupsModel.PersonGroupSharedWithRow> {
    void previous;
    return this.http
      .patch<PersonGroupsModel.PersonGroupSharedWithRow>(
        api.personGroups.accessListItem(groupId, orgId),
        { accessType },
      );
  }

  public removePersonGroupAccessListItem(groupId: string, orgId: string): Observable<void> {
    return this.http.delete<void>(api.personGroups.accessListItem(groupId, orgId));
  }

  public removePersonsFromGroup(groupId: string, personIds: number[]): Observable<number[]> {
    return this.http
      .patch<PersonGroupsModel.PersonGroupDocument>(api.personGroups.persons(groupId), { personIds })
      .pipe(map(() => personIds));
  }

  public addPersonsToGroups(
    payload: PersonGroupsModel.AddPersonsToGroupsAPIPayload,
  ): Observable<PersonGroupsModel.API.AddPersonsToGroupsResponse> {
    return this.http.post<PersonGroupsModel.API.AddPersonsToGroupsResponse>(api.personGroups.addPersons, payload);
  }
}
